import { motion } from "framer-motion";
import { useRef } from "react";
import { ChevronLeft, ChevronRight } from "lucide-react";
import { useLang } from "../context/LanguageContext";
import { copy } from "../data/copy";
import { bestsellers } from "../data/products";
import { ProductCard } from "./ProductCard";

export const Bestsellers = () => {
    const { t, lang } = useLang();
    const scrollRef = useRef(null);
    const items = bestsellers();

    const scroll = (dir) => {
        const el = scrollRef.current;
        if (!el) return;
        el.scrollBy({ left: dir * (el.clientWidth * 0.7), behavior: "smooth" });
    };

    return (
        <section
            className="py-24 md:py-32 bg-[color:var(--kp-bone)]"
            data-testid="bestsellers"
        >
            <div className="max-w-[1400px] mx-auto px-6 md:px-10">
                <div className="flex items-end justify-between gap-4 mb-12">
                    <div>
                        <div className="flex items-center gap-3 mb-4">
                            <span className="w-8 h-px bg-[color:var(--kp-red)]" />
                            <span
                                className={`text-[11px] tracking-mono-wide text-[color:var(--kp-ink)]/60 ${
                                    lang === "te" ? "font-telugu-body" : ""
                                }`}
                            >
                                {t(copy.bestsellers.eyebrow)}
                            </span>
                        </div>
                        <motion.h2
                            initial={{ opacity: 0, y: 30 }}
                            whileInView={{ opacity: 1, y: 0 }}
                            viewport={{ once: true, margin: "-80px" }}
                            transition={{ duration: 0.9, ease: [0.19, 1, 0.22, 1] }}
                            className={`text-[8vw] md:text-[5vw] leading-[0.95] tracking-editorial whitespace-pre-line ${
                                lang === "te" ? "font-telugu-display" : "font-serif-display"
                            }`}
                        >
                            {t(copy.bestsellers.title)
                                .split("\n")
                                .map((line, i) => (
                                    <span key={i} className="block">
                                        {i === 1 ? (
                                            <em className="italic text-[color:var(--kp-red)]">{line}</em>
                                        ) : (
                                            line
                                        )}
                                    </span>
                                ))}
                        </motion.h2>
                    </div>
                    <div className="hidden md:flex gap-2">
                        <button
                            onClick={() => scroll(-1)}
                            className="p-3 border border-[color:var(--kp-ink)]/25 rounded-full hover:border-[color:var(--kp-ink)]"
                            data-testid="bestseller-prev"
                        >
                            <ChevronLeft size={16} />
                        </button>
                        <button
                            onClick={() => scroll(1)}
                            className="p-3 border border-[color:var(--kp-ink)]/25 rounded-full hover:border-[color:var(--kp-ink)]"
                            data-testid="bestseller-next"
                        >
                            <ChevronRight size={16} />
                        </button>
                    </div>
                </div>
                <div
                    ref={scrollRef}
                    className="flex gap-8 overflow-x-auto snap-x snap-mandatory pb-4 -mx-6 md:-mx-10 px-6 md:px-10 scrollbar-hide"
                    style={{ scrollbarWidth: "none" }}
                    data-testid="bestsellers-scroll"
                >
                    {items.map((p, i) => (
                        <div
                            key={p.id}
                            className="min-w-[260px] w-[80vw] sm:w-[42vw] md:w-[28vw] lg:w-[22vw] snap-start"
                        >
                            <ProductCard product={p} index={i} />
                        </div>
                    ))}
                </div>
            </div>
        </section>
    );
};
